import { Dialog } from '@angular/cdk/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { MemberType } from 'src/app/models/company.model';
import { LicenseBatchDto } from 'src/app/models/license.model';
import { AlertService } from 'src/app/services/alert.service';
import { LicenseBatchService } from 'src/app/services/license-batch.service';
import { LicenseBatchStore } from 'src/app/services/license-batch.store';
import { CompanyService } from 'src/app/state-services/company.service';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';
import {
  ActionButtonColumnDefinition,
  ColumnDefinition,
  DataColumnDefinition,
  PaginatorConfig,
} from '../qs-table/column-definition.interface';

type UserTableRow = {
  no: number;
  id: string;
  name: string;
  email: string;
  role: string;
};

@Component({
  selector: 'app-license-manage-users',
  templateUrl: './license-manage-users.component.html',
  styleUrls: ['./license-manage-users.component.scss'],
  standalone: false,
})
export class LicenseManageUsersComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly alertService = inject(AlertService);
  private readonly companyService = inject(CompanyService);
  private readonly licenseBatchService = inject(LicenseBatchService);
  private readonly licenseBatchStore = inject(LicenseBatchStore);

  batch: LicenseBatchDto | null = null;
  linkedUsersRows: UserTableRow[] = [];
  availableUsersRows: UserTableRow[] = [];
  loading = false;
  loadingCompanyUsers = false;
  assignmentInProgress = false;

  readonly paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  private readonly userColumns: ColumnDefinition<UserTableRow>[] = [
    {
      type: 'data',
      field: 'no',
      header: 'No',
      sortFn: (a, b, multiplier) => (a.no - b.no) * multiplier,
    } as DataColumnDefinition<UserTableRow>,
    {
      type: 'data',
      field: 'name',
      header: 'Name',
      sortFn: (a, b, multiplier) => a.name.localeCompare(b.name) * multiplier,
    } as DataColumnDefinition<UserTableRow>,
    {
      type: 'data',
      field: 'email',
      header: 'Email',
      sortFn: (a, b, multiplier) => a.email.localeCompare(b.email) * multiplier,
    } as DataColumnDefinition<UserTableRow>,
    {
      type: 'data',
      field: 'role',
      header: 'Role',
      sortFn: (a, b, multiplier) => a.role.localeCompare(b.role) * multiplier,
    } as DataColumnDefinition<UserTableRow>,
  ];

  readonly linkedColumns: ColumnDefinition<UserTableRow>[] = [
    ...this.userColumns,
    {
      type: 'action',
      actionType: 'button',
      header: 'Unlink',
      iconName: 'link_off',
      buttonName: 'Unlink User',
      buttonCallback: (row) => this.unlinkUser(row),
      isDisabled: () => this.assignmentInProgress,
    } as ActionButtonColumnDefinition<UserTableRow>,
  ];

  readonly availableColumns: ColumnDefinition<UserTableRow>[] = [
    ...this.userColumns,
    {
      type: 'action',
      actionType: 'button',
      header: 'Link',
      iconName: 'link',
      buttonName: 'Link User',
      buttonCallback: (row) => this.linkUser(row),
      isDisabled: () => this.assignmentInProgress,
    } as ActionButtonColumnDefinition<UserTableRow>,
  ];

  ngOnInit(): void {
    const batchId = this.route.snapshot.paramMap.get('id');
    if (!batchId) {
      this.alertService.error('Missing license batch id.');
      this.navigateBack();
      return;
    }

    this.loading = true;
    this.licenseBatchStore
      .getBatchFromStoreOrFetch({
        batchId,
        projection: 'FULL',
        forceRefresh: true,
      })
      .pipe(take(1))
      .subscribe({
        next: (batch) => {
          if (batch.product?.target !== 'USER') {
            this.alertService.error('This license batch targets devices.');
            this.batch = batch;
            this.navigateBack();
            return;
          }
          this.batch = batch;
          this.linkedUsersRows = this.mapLinkedUsers(batch);
          this.loadCompanyUsers(batch);
          this.loading = false;
        },
        error: (error: unknown) => {
          this.loading = false;
          this.alertService.error(this.getErrorMessage(error));
          this.navigateBack();
        },
      });
  }

  get linkedUsersCount(): number {
    return this.linkedUsersRows.length;
  }
  get availableUsersCount(): number {
    return this.availableUsersRows.length;
  }
  get batchTitle(): string {
    return this.batch?.name?.trim() || 'License Batch';
  }
  get batchId(): string {
    return this.batch?.id ?? '-';
  }
  get companyLabel(): string {
    return this.batch?.company?.name || this.batch?.companyId || 'Not assigned';
  }

  navigateBack(): void {
    const base = this.router.url.includes('/admin/')
      ? '/admin/licenses/batches'
      : '/licenses/batches';
    this.router.navigate(this.batch?.id ? [base, this.batch.id] : [base]);
  }

  private loadCompanyUsers(batch: LicenseBatchDto): void {
    const companyId =
      batch.companyId?.trim() || batch.company?.id?.trim() || '';
    if (!companyId) {
      this.alertService.error('The license batch has no company assigned.');
      return;
    }

    this.loadingCompanyUsers = true;
    this.companyService
      .getCompanyMembers(companyId)
      .pipe(take(1))
      .subscribe({
        next: (members) => {
          const linkedIds = new Set(this.linkedUsersRows.map((row) => row.id));
          this.availableUsersRows = this.resequence(
            members
              .filter((member) => !linkedIds.has(member.userId))
              .map((member) => this.toMemberRow(member)),
          );
          this.loadingCompanyUsers = false;
        },
        error: (error: unknown) => {
          this.loadingCompanyUsers = false;
          this.alertService.error(this.getErrorMessage(error));
        },
      });
  }

  private linkUser(row: UserTableRow): void {
    const batchId = this.batch?.id?.trim();
    if (!batchId || this.assignmentInProgress) return;
    if (
      (this.batch?.licenseBatchSize ?? 0) > 0 &&
      this.linkedUsersRows.length >= this.batch!.licenseBatchSize!
    ) {
      this.dialog.open(InformationModalComponent, {
        data: {
          headerText: 'Batch Size Reached',
          bodyText:
            'Unlink one user before linking another user to this batch.',
          cancelable: false,
        },
      });
      return;
    }

    this.assignmentInProgress = true;
    this.licenseBatchService
      .assignUserToBatch(batchId, row.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.availableUsersRows = this.resequence(
            this.availableUsersRows.filter((item) => item.id !== row.id),
          );
          this.linkedUsersRows = this.resequence([
            ...this.linkedUsersRows,
            row,
          ]);
          this.assignmentInProgress = false;
        },
        error: (error: unknown) => this.handleActionError(error),
      });
  }

  private unlinkUser(row: UserTableRow): void {
    const batchId = this.batch?.id?.trim();
    if (!batchId || this.assignmentInProgress) return;
    this.assignmentInProgress = true;
    this.licenseBatchService
      .removeUserFromBatch(batchId, row.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.linkedUsersRows = this.resequence(
            this.linkedUsersRows.filter((item) => item.id !== row.id),
          );
          this.availableUsersRows = this.resequence([
            ...this.availableUsersRows,
            row,
          ]);
          this.assignmentInProgress = false;
        },
        error: (error: unknown) => this.handleActionError(error),
      });
  }

  private mapLinkedUsers(batch: LicenseBatchDto): UserTableRow[] {
    const profiles = new Map(
      (batch.licensedUsers ?? []).map((user) => [user.id, user]),
    );
    const ids = new Set(batch.licensedUserIds ?? []);
    profiles.forEach((_, id) => ids.add(id));
    return this.resequence(
      [...ids].filter(Boolean).map((id) => {
        const user = profiles.get(id);
        return {
          no: 0,
          id,
          name:
            [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '-',
          email: user?.email?.trim() || '-',
          role: '-',
        };
      }),
    );
  }

  private toMemberRow(member: MemberType): UserTableRow {
    return {
      no: 0,
      id: member.userId,
      name:
        [member.firstName, member.lastName].filter(Boolean).join(' ') || '-',
      email: member.email || '-',
      role: member.role,
    };
  }

  private resequence(rows: UserTableRow[]): UserTableRow[] {
    return rows.map((row, index) => ({ ...row, no: index + 1 }));
  }

  private handleActionError(error: unknown): void {
    this.assignmentInProgress = false;
    this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Action Failed',
        bodyText: this.getErrorMessage(error),
        cancelable: false,
      },
    });
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object') {
      const value = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      return (
        value.error?.message ||
        value.error?.title ||
        value.message ||
        'License user operation failed.'
      );
    }
    return 'License user operation failed.';
  }
}
